/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * XChaCha20-Poly1305 AEAD wrapper. Wraps `@noble/ciphers/chacha.js` to
 * enforce key/nonce sizes at the boundary and surface typed errors.
 *
 * XChaCha20-Poly1305 was chosen over AES-GCM because the 24-byte nonce
 * makes random nonce sampling collision-safe at any realistic message
 * volume — no counter discipline, no key-rotation bookkeeping.
 */

import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';

const KEY_LENGTH_BYTES = 32;
const NONCE_LENGTH_BYTES = 24;

/** Thrown when a key of the wrong length is supplied to `encrypt`/`decrypt`. */
export class InvalidKeySizeError extends Error {
  constructor(actual: number) {
    super(
      `XChaCha20-Poly1305 key must be ${KEY_LENGTH_BYTES} bytes, got ${actual}`
    );
    this.name = 'InvalidKeySizeError';
  }
}

/** Thrown when a nonce of the wrong length is supplied to `encrypt`/`decrypt`. */
export class InvalidNonceError extends Error {
  constructor(actual: number) {
    super(
      `XChaCha20-Poly1305 nonce must be ${NONCE_LENGTH_BYTES} bytes, got ${actual}`
    );
    this.name = 'InvalidNonceError';
  }
}

/**
 * Thrown by `decrypt` when XChaCha20-Poly1305 authentication fails —
 * tampered ciphertext, wrong key, wrong nonce, modified AAD, or
 * missing AAD when one was bound at encrypt time. The underlying
 * `@noble/ciphers` library throws an implementation-specific `Error`
 * on tag-verify failure; wrapping it in a typed class lets callers
 * `instanceof`-discriminate authentication failures from caller bugs
 * (`InvalidKeySizeError` / `InvalidNonceError`, raised by
 * `validateKeyAndNonce` *before* the AEAD primitive runs) and from
 * unrelated faults — without grepping `error.message` (#105).
 *
 * The original noble error is attached via `cause` so a developer
 * debugging an unexpected failure still has the chain.
 */
export class AeadAuthenticationError extends Error {
  constructor(
    message = 'XChaCha20-Poly1305 authentication failed',
    options?: ErrorOptions
  ) {
    super(message, options);
    this.name = 'AeadAuthenticationError';
  }
}

export interface AeadEncryptParams {
  /** 32-byte symmetric key. */
  key: Uint8Array;
  /** 24-byte nonce — sample with the F3 RNG seam. */
  nonce: Uint8Array;
  /** Plaintext to encrypt (any length, may be empty). */
  plaintext: Uint8Array;
  /** Optional associated data; integrity-protected, not encrypted. */
  aad?: Uint8Array;
}

export interface AeadDecryptParams {
  /** 32-byte symmetric key. */
  key: Uint8Array;
  /** 24-byte nonce that was used at encrypt time. */
  nonce: Uint8Array;
  /** Ciphertext including the 16-byte Poly1305 tag suffix. */
  ciphertext: Uint8Array;
  /** Associated data exactly as supplied at encrypt time. */
  aad?: Uint8Array;
}

const validateKeyAndNonce = (key: Uint8Array, nonce: Uint8Array): void => {
  if (key.length !== KEY_LENGTH_BYTES) {
    throw new InvalidKeySizeError(key.length);
  }
  if (nonce.length !== NONCE_LENGTH_BYTES) {
    throw new InvalidNonceError(nonce.length);
  }
};

/**
 * Encrypts plaintext under XChaCha20-Poly1305.
 *
 * @param params - 32-byte key, 24-byte nonce, plaintext, optional AAD
 * @returns ciphertext concatenated with the 16-byte Poly1305 tag
 * @throws {InvalidKeySizeError} if `key.length !== 32`
 * @throws {InvalidNonceError} if `nonce.length !== 24`
 */
export const encrypt = (params: AeadEncryptParams): Uint8Array => {
  const { key, nonce, plaintext, aad } = params;
  validateKeyAndNonce(key, nonce);
  return xchacha20poly1305(key, nonce, aad).encrypt(plaintext);
};

/**
 * Decrypts XChaCha20-Poly1305 ciphertext.
 *
 * @param params - 32-byte key, 24-byte nonce, ct||tag, optional AAD
 * @returns plaintext bytes
 * @throws {InvalidKeySizeError} if `key.length !== 32` (caller bug —
 *   raised *before* the AEAD primitive runs)
 * @throws {InvalidNonceError} if `nonce.length !== 24` (same)
 * @throws {AeadAuthenticationError} if the Poly1305 tag does not
 *   verify — tampered ciphertext, wrong key, wrong nonce, modified
 *   or missing AAD. The original noble error is attached via
 *   `error.cause`.
 */
export const decrypt = (params: AeadDecryptParams): Uint8Array => {
  const { key, nonce, ciphertext, aad } = params;
  validateKeyAndNonce(key, nonce);
  try {
    return xchacha20poly1305(key, nonce, aad).decrypt(ciphertext);
  } catch (cause) {
    // Any throw out of noble's `.decrypt(...)` is a tag-verify
    // failure — by construction, key and nonce sizes have already
    // passed `validateKeyAndNonce`, so the only remaining failure
    // mode is authentication. Wrap as a typed error so callers
    // (routing-sheet `unwrapForRecipient`, document AAD path, etc.)
    // can `instanceof`-discriminate without grepping messages.
    throw new AeadAuthenticationError(undefined, { cause });
  }
};
